import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import SiteAccessGate from '../components/SiteAccessGate';
import { DEV_SKIP_LOGIN } from '../config/devBypass';

const AuthContext = createContext(null);

const DEV_PLACEHOLDER_USER = {
  id: 0,
  email: 'dev@local.test',
  first_name: 'Dev',
  last_name: 'Lokal',
  is_staff: true,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteGateRequired, setSiteGateRequired] = useState(false);
  const [authProvider, setAuthProvider] = useState('local');

  const loadAuthConfig = useCallback(async () => {
    try {
      const res = await authAPI.config();
      const provider = res.data?.provider === 'entra' ? 'entra' : 'local';
      setAuthProvider(provider);
      return provider;
    } catch {
      setAuthProvider('local');
      return 'local';
    }
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const provider = await loadAuthConfig();
      if (DEV_SKIP_LOGIN) {
        // Vorschau-Zugang (SITE_ACCESS_PASSWORD) gilt auch im Dev-Bypass – sonst 403 auf allen API-Calls.
        try {
          const statusRes = await authAPI.siteAccessStatus();
          const { enabled, unlocked } = statusRes.data;
          if (enabled && !unlocked) {
            setSiteGateRequired(true);
            setUser(null);
            return;
          }
        } catch {
          /* Backend offline – ohne Gate weiter */
        }
        setSiteGateRequired(false);
        try {
          await authAPI.getCsrf();
        } catch {
          /* optional lokal ohne Backend */
        }
        setUser(DEV_PLACEHOLDER_USER);
        return;
      }
      const statusRes = await authAPI.siteAccessStatus();
      const { enabled, unlocked } = statusRes.data;
      if (enabled && !unlocked) {
        setSiteGateRequired(true);
        setUser(null);
        return;
      }
      setSiteGateRequired(false);
      await authAPI.getCsrf();
      try {
        const res = await authAPI.me();
        setUser(res.data);
      } catch {
        if (provider === 'entra') {
          setUser(null);
          return;
        }
        setUser(null);
      }
    } catch {
      setUser(null);
      setSiteGateRequired(false);
    } finally {
      setLoading(false);
    }
  }, [loadAuthConfig]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const handleSiteUnlock = useCallback(
    async (password) => {
      await authAPI.siteAccessUnlock(password);
      await bootstrap();
    },
    [bootstrap],
  );

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login(email, password);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      /* ohne Session unkritisch */
    }
    setUser(DEV_SKIP_LOGIN ? DEV_PLACEHOLDER_USER : null);
  }, []);

  const startEntraLogin = useCallback(async () => {
    const res = await authAPI.entraStart();
    const authUrl = res.data?.auth_url;
    if (!authUrl) throw new Error('Keine Microsoft-Login-URL erhalten.');
    window.location.assign(authUrl);
  }, []);

  const completeEntraLogin = useCallback(async (code, state) => {
    const res = await authAPI.entraComplete(code, state);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  if (siteGateRequired) {
    return <SiteAccessGate onUnlock={handleSiteUnlock} />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        authProvider,
        startEntraLogin,
        completeEntraLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden.');
  return ctx;
}
